import React, { useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { branding } from '../config/branding';

type AssociationLogoProps = {
  size?: number;
  showText?: boolean;
  variant?: 'horizontal' | 'stacked';
  textColor?: string;
  subtitleColor?: string;
  expandOnClick?: boolean;
};

const AssociationLogo: React.FC<AssociationLogoProps> = ({
  size = 48,
  showText = true,
  variant = 'horizontal',
  textColor = 'text.primary',
  subtitleColor = 'text.secondary',
  expandOnClick = false,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const isStacked = variant === 'stacked';

  const logoImage = (
    <Box
      component="img"
      src={branding.logoSrc}
      alt={`Logo ${branding.shortName}`}
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        objectFit: 'contain',
        flexShrink: 0,
        bgcolor: '#fff',
        boxShadow: '0 2px 8px rgba(27, 67, 50, 0.12)',
        ...(expandOnClick && {
          cursor: 'pointer',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            transform: 'scale(1.04)',
            boxShadow: '0 4px 12px rgba(27, 67, 50, 0.2)',
          },
        }),
      }}
      onClick={expandOnClick ? () => setPreviewOpen(true) : undefined}
      role={expandOnClick ? 'button' : undefined}
      tabIndex={expandOnClick ? 0 : undefined}
      onKeyDown={
        expandOnClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPreviewOpen(true);
              }
            }
          : undefined
      }
    />
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: isStacked ? 'column' : 'row',
          gap: isStacked ? 1.5 : 1.25,
          textAlign: isStacked ? 'center' : 'left',
          minWidth: 0,
        }}
      >
        {logoImage}
        {showText && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isStacked ? 'h5' : 'subtitle1'}
              component="div"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                color: textColor,
                letterSpacing: 0.2,
              }}
            >
              {branding.shortName}
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: subtitleColor,
                lineHeight: 1.3,
                display: 'block',
                mt: 0.25,
              }}
            >
              {isStacked ? branding.fullName : branding.tagline}
            </Typography>
          </Box>
        )}
      </Box>

      {expandOnClick && (
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: '#fff',
            },
          }}
        >
          <Box sx={{ position: 'relative', p: { xs: 2, sm: 3 }, textAlign: 'center' }}>
            <IconButton
              aria-label="Fechar"
              onClick={() => setPreviewOpen(false)}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <Close />
            </IconButton>
            <Box
              component="img"
              src={branding.logoSrc}
              alt={`Logo ${branding.shortName}`}
              sx={{
                width: '100%',
                maxWidth: 420,
                height: 'auto',
                objectFit: 'contain',
                mx: 'auto',
                display: 'block',
              }}
            />
            <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 700, color: branding.colors.primary }}>
              {branding.shortName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {branding.fullName}
            </Typography>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default AssociationLogo;
